"use client";

import React from "react";
import { motion } from "framer-motion";

import Image from "next/image";

import { GroqImage } from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

import noise from "@/public/global/nice-noise.svg";
import cardBG from "@/public/krypto-day/card-bg.jpg";

import bgGrid from "@/public/frankfurt-event/bgGrid.svg";

export default function Content({
  data,
}: {
  data: PageEventSchema | PageKryptoDaySchema;
}) {
  return (
    <section className="relative pb-[10rem] overflow-clip lg:pb-[18rem]">
      <div className="px-[2rem] w-[min(179rem,100%)]  mx-auto flex flex-col items-center">
        {data?.summaryTitles && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <PortableText
              value={data?.summaryTitles ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="text-[4rem] lg:text-[7rem] font-[600] tracking-[-.14rem] gradient-title !text-white/80 w-full text-center mb-[4rem] lg:mb-[6rem]">
                      {children}
                    </h2>
                  ),
                  h6: ({ children }) => (
                    <div className="mx-auto font-bold uppercase bg-white bg-opacity-10 rounded-full border border-white border-opacity-20 w-fit px-[2rem] py-[1.5rem] leading-[1rem] text-[1.75rem] backdrop-blur-[2rem] mb-[2rem]">
                      {children}
                    </div>
                  ),
                },
              })}
            />
          </motion.div>
        )}

        <div className="grid grid-cols-1 w-full lg:grid-cols-3 gap-[2rem]">
          {data?.summaryCards &&
            data?.summaryCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`flex overflow-clip flex-col items-center justify-between relative rounded-[2rem] gradient-border-white-opacity bg-gradient-to-b from-white/[7%] to-white/[3%] ${index === 1 ? "lg:col-span-2" : ""} h-[67rem]`}
              >
                {/* <Image src={noise} alt="" className="absolute z-[1]" /> */}

                {/* <div className="absolute inset-0 w-full h-full z-[1]"> */}
                {/*   <svg id="noice" className="w-full h-full mix-blend-overlay"> */}
                {/*     <filter id="noise-filter"> */}
                {/*       <feTurbulence */}
                {/*         type="fractalNoise" */}
                {/*         baseFrequency="0.84" */}
                {/*         numOctaves="4" */}
                {/*         stitchTiles="stitch" */}
                {/*       ></feTurbulence> */}
                {/*       <feColorMatrix type="saturate" values="0"></feColorMatrix> */}
                {/*       <feComponentTransfer> */}
                {/*         <feFuncR type="linear" slope="0.28"></feFuncR> */}
                {/*         <feFuncG type="linear" slope="0.28"></feFuncG> */}
                {/*         <feFuncB type="linear" slope="0.28"></feFuncB> */}
                {/*         <feFuncA type="linear" slope="0.79"></feFuncA> */}
                {/*       </feComponentTransfer> */}
                {/*       <feComponentTransfer> */}
                {/*         <feFuncR type="linear" slope="1.53" intercept="-0.27" /> */}
                {/*         <feFuncG type="linear" slope="1.53" intercept="-0.27" /> */}
                {/*         <feFuncB type="linear" slope="1.53" intercept="-0.27" /> */}
                {/*       </feComponentTransfer> */}
                {/*     </filter> */}
                {/*     <rect */}
                {/*       width="100%" */}
                {/*       height="100%" */}
                {/*       filter="url(#noise-filter)" */}
                {/*     ></rect> */}
                {/*   </svg> */}
                {/* </div> */}

                {index === 1 && (
                  <div className="absolute inset-0 w-full h-full z-[2]">
                    <Image
                      src={cardBG}
                      alt=""
                      quality={100}
                      className="!w-full !h-full object-cover"
                    />
                  </div>
                )}

                <div
                  className={`flex relative flex-col p-[4rem] gap-[1rem] z-[5] `}
                >
                  <h4 className="text-white text-[3rem] leading-[100%]">
                    {card.title}
                  </h4>
                  <p
                    className={`text-white/70 font-[400] text-[2rem] leading-[120%] ${index === 1 && "w-[min(42.625rem,100%)]"}`}
                  >
                    {card.text}
                  </p>
                </div>
                <GroqImage
                  className={` absolute bottom-0 z-[4] ${index === 1 ? "object-cover !w-[59.875rem]" : "!w-full"}`}
                  src={card.image}
                  quality={100}
                  alt="asset"
                />
                {/* blur */}
                {/* <div className="absolute left-1/2 rounded-full opacity-10 -translate-x-1/2 pointer-events-none bottom-[-10rem] bg-[#1ECC97] w-[50rem] h-[50rem] blur-[8.5rem]"></div> */}
                {/* end blur */}
              </motion.div>
            ))}
        </div>

        {"contentCalloutText" in data && data.contentCalloutText && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col justify-between items-center w-full bg-gradient-to-r lg:flex-row lg:gap-0 from-[#61FFCE]/30 to-[#007A54]/30 rounded-[2.5rem] pl-[3rem] py-[2rem] pr-[3rem] gap-[2rem]"
          >
            <div className="richtext lg:richtext-start prose-p:m-0 prose-strong:text-white prose-strong:font-[400] w-[min(73.125rem,100%)]">
              <PortableText
                value={data.contentCalloutText}
                components={customRichTextComponent({})}
              />
            </div>
            {"contentCalloutCTA" in data && data.contentCalloutCTA && (
              <div className="[&_*]:font-sans [&_*]:uppercase">
                <CTAButton data={data.contentCalloutCTA} className="" />
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
